// Command palette model (UX spec 6.1): what the palette offers for a query, in which order, and how
// the arrow keys move through it. Pure, so it is tested without a browser; app/CommandPalette.tsx
// supplies the workspace's pages, people and calls and renders the result.
import type { Density } from './density.ts';
import type { ThemePreference } from './theme.ts';

export type PaletteGroupId = 'actions' | 'teammates' | 'calls' | 'pages';

export const GROUP_TITLES: Record<PaletteGroupId, string> = {
  actions: 'Actions',
  teammates: 'Call a teammate',
  calls: 'Calls',
  pages: 'Go to',
};
const GROUP_ORDER: readonly PaletteGroupId[] = ['actions', 'teammates', 'calls', 'pages'];

/** What an item does when chosen; the palette component carries it out. */
export type PaletteCommand =
  | { kind: 'navigate'; to: string }
  | { kind: 'call_teammate'; userId: string | null }
  | { kind: 'dial'; input: string }
  | { kind: 'set_status'; status: string }
  | { kind: 'set_theme'; theme: ThemePreference }
  | { kind: 'set_density'; density: Density }
  | { kind: 'show_shortcuts' };

export type PaletteIcon = 'page' | 'phone' | 'dial' | 'call' | 'status' | 'theme-day' | 'theme-night' | 'theme-system' | 'density' | 'keyboard';

export interface PaletteItem {
  id: string;
  group: PaletteGroupId;
  label: string;
  /** A second line: who, when, why. */
  detail?: string;
  /** Right-aligned words: the shortcut or the kind of result. */
  hint?: string;
  /** Extra words the query matches but the row doesn't show. */
  keywords?: string;
  icon: PaletteIcon;
  command: PaletteCommand;
}

export interface PaletteGroup {
  id: PaletteGroupId;
  title: string;
  items: PaletteItem[];
}

export interface PaletteSource {
  /** Destinations from the nav (app/nav.tsx useNav) plus Settings. */
  pages: readonly { to: string; label: string }[];
  /** Teammates who can be called (not you, active). Empty when internal calling isn't offered. */
  teammates: readonly { id: string; name: string; presence?: string }[];
  /** Calls in the loaded ledger, newest first. */
  calls: readonly { id: string; party: string; detail: string; numbers: readonly string[] }[];
  canCallTeammates: boolean;
  /** Outside calls: offered whenever the capability isn't hidden; the dialer explains a block. */
  dial: { offered: boolean; blockedReason: string | null };
  /** A phone number typed into the palette, parsed by the caller (libphonenumber). */
  typedNumber: { input: string; display: string } | null;
  status: { current: string; options: readonly { value: string; label: string }[] } | null;
  theme: { preference: ThemePreference; showing: 'day' | 'night' };
  density: Density;
  /** Shortcut text for the help item, already in this platform's words. */
  helpHint: string | null;
}

export const LIMITS = { teammates: 6, calls: 6 } as const;

const fold = (text: string) => text.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const digits = (text: string) => text.replace(/\D/g, '');

/**
 * How well an item matches: 0 is no match; higher is better. Every word of the query must appear;
 * the label starting with the query beats a word in the label starting with it, which beats any
 * other place. Digit queries also match phone numbers digit by digit.
 */
export function matchScore(query: string, label: string, extra = '', numbers: readonly string[] = []): number {
  const q = fold(query.trim());
  if (!q) return 1;
  const l = fold(label);
  const all = `${l} ${fold(extra)}`;
  const words = q.split(/\s+/).filter(Boolean);
  const qDigits = digits(q);
  const numberHit = qDigits.length >= 3 && /^[\d\s()+.-]+$/.test(q) && numbers.some(n => digits(n).includes(qDigits));
  if (!numberHit && !words.every(w => all.includes(w))) return 0;
  if (l.startsWith(q)) return 4;
  if (l.split(/[\s,]+/).some(word => word.startsWith(words[0] ?? q))) return 3;
  if (numberHit) return 3;
  return 2;
}

export function paletteGroups(query: string, source: PaletteSource): PaletteGroup[] {
  const q = query.trim();
  const actions: PaletteItem[] = [];
  if (source.dial.offered) {
    if (source.typedNumber) {
      actions.push({ id: 'dial-typed', group: 'actions', label: `Dial ${source.typedNumber.display}`, detail: source.dial.blockedReason ?? 'Fills in the dialer. Choose the number to call from, then press Call.', icon: 'dial', command: { kind: 'dial', input: source.typedNumber.input } });
    }
    actions.push({ id: 'dial', group: 'actions', label: 'Dial a number', keywords: 'call outside phone number pstn keypad', ...(source.dial.blockedReason ? { detail: source.dial.blockedReason } : {}), icon: 'dial', command: { kind: 'dial', input: '' } });
  }
  if (source.canCallTeammates) actions.push({ id: 'call-teammate', group: 'actions', label: 'Call a teammate', keywords: 'internal dial phone', icon: 'phone', command: { kind: 'call_teammate', userId: null } });
  if (source.status) {
    // Unprompted, availability is one toggle (Available and Away); typing reaches every other status.
    const toggle = source.status.current === 'available' ? 'away' : 'available';
    for (const option of source.status.options) {
      if (option.value === source.status.current || (!q && option.value !== toggle)) continue;
      actions.push({ id: `status-${option.value}`, group: 'actions', label: `Set status to ${option.label}`, keywords: 'availability presence', icon: 'status', command: { kind: 'set_status', status: option.value } });
    }
  }
  const other = source.theme.showing === 'night' ? 'day' : 'night';
  actions.push({ id: `theme-${other}`, group: 'actions', label: other === 'night' ? 'Switch to the Night theme' : 'Switch to the Day theme', keywords: 'appearance dark light mode theme', icon: other === 'night' ? 'theme-night' : 'theme-day', command: { kind: 'set_theme', theme: other } });
  if (source.theme.preference !== 'system') actions.push({ id: 'theme-system', group: 'actions', label: 'Match the system theme', keywords: 'appearance dark light mode automatic', icon: 'theme-system', command: { kind: 'set_theme', theme: 'system' } });
  const density: Density = source.density === 'compact' ? 'comfortable' : 'compact';
  actions.push({ id: `density-${density}`, group: 'actions', label: density === 'compact' ? 'Use compact density' : 'Use comfortable density', keywords: 'appearance spacing rows dense', icon: 'density', command: { kind: 'set_density', density } });
  actions.push({ id: 'shortcuts', group: 'actions', label: 'Show keyboard shortcuts', keywords: 'help keys hotkeys', ...(source.helpHint ? { hint: source.helpHint } : {}), icon: 'keyboard', command: { kind: 'show_shortcuts' } });

  const teammates: PaletteItem[] = q && source.canCallTeammates
    ? source.teammates.map(t => ({ id: `teammate-${t.id}`, group: 'teammates' as const, label: `Call ${t.name}`, ...(t.presence ? { detail: t.presence } : {}), keywords: t.name, icon: 'phone' as const, command: { kind: 'call_teammate' as const, userId: t.id } }))
    : [];
  const calls: PaletteItem[] = q
    ? source.calls.map(c => ({ id: `call-${c.id}`, group: 'calls' as const, label: `Open call with ${c.party}`, detail: c.detail, keywords: c.numbers.join(' '), icon: 'call' as const, command: { kind: 'navigate' as const, to: `/inbox/calls/${encodeURIComponent(c.id)}` } }))
    : [];
  const pages: PaletteItem[] = source.pages.map(p => ({ id: `page-${p.to}`, group: 'pages', label: p.label, keywords: `go to open page ${p.label}`, icon: 'page', command: { kind: 'navigate', to: p.to } }));

  const numbersFor = new Map<string, readonly string[]>(source.calls.map(c => [`call-${c.id}`, c.numbers]));
  const pick = (items: PaletteItem[], limit = Infinity) => {
    // A typed number always offers the dial action, even though its words don't match the label.
    const scored = items
      .map((item, index) => ({ item, index, score: item.id === 'dial-typed' ? 5 : matchScore(q, item.label, `${item.keywords ?? ''} ${item.detail ?? ''}`, numbersFor.get(item.id) ?? []) }))
      .filter(s => s.score > 0);
    if (q) scored.sort((a, b) => b.score - a.score || a.index - b.index);
    return scored.slice(0, limit).map(s => s.item);
  };
  const byGroup: Record<PaletteGroupId, PaletteItem[]> = {
    actions: pick(actions),
    teammates: pick(teammates, LIMITS.teammates),
    calls: pick(calls, LIMITS.calls),
    pages: pick(pages),
  };
  return GROUP_ORDER.map(id => ({ id, title: GROUP_TITLES[id], items: byGroup[id] })).filter(g => g.items.length > 0);
}

export function flatItems(groups: readonly PaletteGroup[]): PaletteItem[] {
  return groups.flatMap(g => g.items);
}

/** The next active row for a key, wrapping at the ends; null for keys the list doesn't take. */
export function moveActive(key: string, index: number, count: number): number | null {
  if (count <= 0) return null;
  switch (key) {
    case 'ArrowDown':
      return (index + 1) % count;
    case 'ArrowUp':
      return (index - 1 + count) % count;
    default:
      return null;
  }
}

/** What a screen reader hears after each keystroke in the field. */
export function resultsAnnouncement(count: number, query: string): string {
  if (count === 0) return query.trim() ? `No results for ${query.trim()}` : 'No results';
  return count === 1 ? '1 result' : `${count} results`;
}
